import { useEffect, useState } from "react";
import { ShowBase, useRecordContext, useGetList, useNotify, useRefresh } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Phone, MessageSquare, PhoneOff } from "lucide-react";
import { useTelephony } from "./useTelephony";
import { formatPhone } from "../me-ui";
import { PILLARS, computeTemperature, type Pillar, type Temp } from "./qualify";
import { Checkbox } from "@/components/ui/checkbox";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { RecordingPlayer } from "../telephony/RecordingPlayer";
import { useNavigate } from "react-router-dom";

const ACTIVITY_LABEL: Record<string, string> = {
  call: "Call", sms: "SMS", email: "Email", rvm: "Voicemail",
  direct_mail: "Mail", note: "Note", task: "Task", status_change: "Status",
};
const tempColor: Record<string, string> = {
  hot: "bg-red-100 text-red-800", warm: "bg-amber-100 text-amber-800", cold: "bg-sky-100 text-sky-800",
};
const DISPOSITIONS = ["correct", "no_answer", "wrong", "dead", "dnc"] as const;

// ---- phones (scored, best-first) + per-number disposition -------------------
type PhoneRow = { id: string; number_e164: string; activity_score: number | null; status: string | null; is_primary: boolean; dnc: boolean };

const Phones = ({ contactId, onCall }: { contactId: string | null; onCall: (n: string) => void }) => {
  const [phones, setPhones] = useState<PhoneRow[]>([]);
  const notify = useNotify();
  const load = () => {
    if (!contactId) return;
    getSupabaseClient().from("contact_phones").select("*").eq("contact_id", contactId)
      .order("activity_score", { ascending: false, nullsFirst: false })
      .order("is_primary", { ascending: false })
      .then(({ data }) => setPhones((data as PhoneRow[]) ?? []));
  };
  useEffect(load, [contactId]);

  const disposition = async (id: string, status: string) => {
    await getSupabaseClient().from("contact_phones").update({ status }).eq("id", id);
    notify("Number dispositioned", { type: "success" });
    load();
  };

  if (!contactId) return <p className="text-sm text-muted-foreground">No contact.</p>;
  if (!phones.length) return <p className="text-sm text-muted-foreground">No numbers on file.</p>;

  return (
    <ul className="space-y-2">
      {phones.map((p) => (
        <li key={p.id} className="flex items-center gap-2 text-sm border-b pb-2">
          <button className="font-mono hover:underline" onClick={() => onCall(p.number_e164)} title="Call this number">
            {formatPhone(p.number_e164)}
          </button>
          <span className="text-xs text-muted-foreground">score {p.activity_score ?? "—"}</span>
          {p.is_primary && <Badge variant="outline" className="text-[0.65rem]">primary</Badge>}
          <select
            className="ml-auto border rounded px-1 py-0.5 text-xs bg-background"
            value={p.status ?? ""}
            onChange={(e) => disposition(p.id, e.target.value)}
          >
            <option value="">untried</option>
            {DISPOSITIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </li>
      ))}
    </ul>
  );
};

// ---- 4-Pillars qualification -> temperature -> outcome ----------------------
const Qualify = ({ record }: { record: any }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [scores, setScores] = useState<Partial<Record<Pillar, Temp>>>({});
  const [hardDeadline, setHardDeadline] = useState(false);
  const [override, setOverride] = useState<Temp | "">("");
  const [saving, setSaving] = useState(false);
  // D22: only Timeline + Reason (+ hard deadline) drive temperature; Price/Condition are deal-quality.
  const computed = computeTemperature({ scores, hardDeadline });
  const finalTemp = (override || computed) as Temp | null;

  const setStatus = async (status: string, temperature: Temp | null, note: string) => {
    setSaving(true);
    // Setting temperature fires the DB trigger -> next_action_at from cadence config.
    const patch: any = { status };
    if (temperature) patch.temperature = temperature;
    // Persist the deal-quality pillars on the lead's intake (they inform the offer, not cadence).
    if (Object.keys(scores).length) {
      patch.intake = { ...(record.intake ?? {}), qualification: { ...scores, hard_deadline: hardDeadline } };
    }
    const { error } = await getSupabaseClient().from("leads").update(patch).eq("id", record.id);
    if (!error) {
      await getSupabaseClient().from("activities").insert({
        type: "status_change", direction: "none", lead_id: record.id,
        contact_id: record.primary_contact_id, body: note,
      });
    }
    setSaving(false);
    if (error) { notify(`Failed: ${error.message}`, { type: "error" }); return; }
    notify(note, { type: "success" });
    refresh();
  };

  const pillarRow = (p: (typeof PILLARS)[number]) => (
    <div key={p.key} className="flex items-center gap-2">
      <span className="text-sm w-32">
        {p.label}
        {p.drives === "deal" && <span className="ml-1 text-[0.6rem] text-muted-foreground uppercase">deal</span>}
      </span>
      <div className="flex gap-1">
        {(["hot", "warm", "cold"] as Temp[]).map((t) => (
          <button
            key={t}
            className={`px-2 py-1 rounded text-xs capitalize border ${scores[p.key] === t ? tempColor[t] + " border-transparent" : "border-input text-muted-foreground"}`}
            onClick={() => setScores((s) => ({ ...s, [p.key]: t }))}
            title={p.hint[t]}
          >{t}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">Timeline + Reason set the follow-up cadence. Price &amp; Condition are deal-quality (they inform the offer, not how often we follow up).</div>
      {PILLARS.filter((p) => p.drives === "cadence").map(pillarRow)}
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={hardDeadline} onCheckedChange={(v) => setHardDeadline(!!v)} />
        Hard deadline within 30 days (foreclosure auction / tax sale) → forces Hot
      </label>
      <div className="border-t pt-2">{PILLARS.filter((p) => p.drives === "deal").map(pillarRow)}</div>
      <div className="flex items-center gap-2 pt-1 text-sm">
        <span>Temperature:</span>
        {computed ? <Badge className={`capitalize ${tempColor[computed]}`}>{computed}</Badge> : <span className="text-muted-foreground">set Timeline</span>}
        <span className="text-muted-foreground ml-2">override:</span>
        <select className="border rounded px-1 py-0.5 text-xs bg-background" value={override} onChange={(e) => setOverride(e.target.value as Temp | "")}>
          <option value="">(use computed)</option>
          <option value="hot">hot</option><option value="warm">warm</option><option value="cold">cold</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {(record.status === "not_interested" || record.status === "ghosting") && (
          <Button variant="secondary" disabled={saving}
            onClick={() => setStatus("new_lead", null, "Reactivated → New Lead")}>
            ♻ Reactivate → New Lead
          </Button>
        )}
        <Button
          disabled={!finalTemp || saving}
          onClick={() => finalTemp && setStatus("qualified", finalTemp, `Qualified — temperature ${finalTemp}`)}
        >Qualify ({finalTemp ?? "…"})</Button>
        <Button variant="outline" disabled={saving} onClick={() => setStatus("not_interested", null, "Marked Not Interested")}>Not Interested</Button>
        <Button variant="outline" disabled={saving} onClick={() => setStatus("dead", null, "Marked Dead")}>Dead</Button>
      </div>
    </div>
  );
};

const ActivityLog = ({ leadId }: { leadId: string }) => {
  const { data: activities = [], isPending } = useGetList("activities", {
    filter: { lead_id: leadId }, sort: { field: "occurred_at", order: "DESC" },
    pagination: { page: 1, perPage: 100 },
  });
  if (isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!activities.length) return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  return (
    <ul className="space-y-2">
      {activities.map((a: any) => (
        <li key={a.id} className="flex gap-2 text-sm border-b pb-2">
          <Badge variant="outline" className="shrink-0">{ACTIVITY_LABEL[a.type] ?? a.type}</Badge>
          <div className="flex-1">
            <div>{a.body || a.disposition || "—"}</div>
            <div className="text-xs text-muted-foreground">
              {a.direction !== "none" ? `${a.direction} · ` : ""}{new Date(a.occurred_at).toLocaleString()}
            </div>
            {a.recording_url && <div className="mt-1"><RecordingPlayer recordingUrl={a.recording_url} /></div>}
          </div>
        </li>
      ))}
    </ul>
  );
};

const ContactActions = ({ record }: { record: any }) => {
  const { call, hangup, sendSms, callState } = useTelephony();
  const notify = useNotify();
  const refresh = useRefresh();
  const [smsText, setSmsText] = useState("");
  const [sending, setSending] = useState(false);
  const phone = record.primary_phone as string | null;
  const inCall = callState === "connecting" || callState === "ringing" || callState === "open";

  const doSms = async () => {
    if (!phone || !smsText.trim()) return;
    setSending(true);
    try {
      await sendSms({ to: phone, body: smsText.trim(), lead_id: record.id, contact_id: record.primary_contact_id });
      notify("Text sent", { type: "success" }); setSmsText(""); refresh();
    } catch (e: any) { notify(`Text failed: ${e.message}`, { type: "error" }); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-3">
      {!inCall ? (
        <Button className="w-full" disabled={!phone} onClick={() => phone && call(phone, record.id)}>
          <Phone className="mr-2 h-4 w-4" /> Call best number
        </Button>
      ) : (
        <Button className="w-full" variant="destructive" onClick={hangup}>
          <PhoneOff className="mr-2 h-4 w-4" />{callState === "open" ? "Hang up" : `${callState}…`}
        </Button>
      )}
      <div className="space-y-2">
        <Input placeholder="Type a text…" value={smsText} disabled={!phone || sending}
          onChange={(e) => setSmsText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") doSms(); }} />
        <Button className="w-full" variant="outline" disabled={!phone || !smsText.trim() || sending} onClick={doSms}>
          <MessageSquare className="mr-2 h-4 w-4" /> {sending ? "Sending…" : "Send Text"}
        </Button>
      </div>
    </div>
  );
};

// Activate Drip (WS-2 / D29) — enroll the lead into a campaign; the runner steps
// it as drafts-for-approval. Enroll via the enroll_lead_in_drip RPC (computes the
// step-1 due time); stop via a direct enrollment update.
const LeadDrip = ({ record }: { record: any }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [enrollment, setEnrollment] = useState<any | null>(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    getSupabaseClient().from("drip_campaigns").select("id,name").eq("entity_type", "lead").eq("is_active", true).order("name")
      .then(({ data }) => setCampaigns(data ?? []));
    getSupabaseClient().from("drip_enrollments").select("id,current_step,campaign_id,drip_campaigns(name)")
      .eq("lead_id", record.id).eq("state", "active").maybeSingle()
      .then(({ data }) => setEnrollment(data));
  };
  useEffect(load, [record.id]);

  const activate = async () => {
    if (!pick) return;
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("enroll_lead_in_drip", { p_lead_id: record.id, p_campaign_ref: pick, p_by: "manual" });
    setBusy(false);
    if (error) { notify(`Activate failed: ${error.message}`, { type: "error" }); return; }
    notify("Drip activated", { type: "success" }); setPick(""); load(); refresh();
  };
  const stop = async () => {
    if (!enrollment) return;
    setBusy(true);
    await getSupabaseClient().from("drip_enrollments").update({ state: "stopped", stop_reason: "manual" }).eq("id", enrollment.id);
    setBusy(false);
    notify("Drip stopped", { type: "success" }); load();
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Drip campaign</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {enrollment ? (
          <div className="flex items-center gap-2 text-sm">
            <Badge className="bg-green-100 text-green-800">active</Badge>
            <span>{enrollment.drip_campaigns?.name ?? "campaign"} · step {enrollment.current_step}</span>
            <Button size="sm" variant="outline" className="ml-auto" disabled={busy} onClick={stop}>Stop</Button>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-xs text-muted-foreground">No active lead campaigns — create one in Settings → Campaigns.</div>
        ) : (
          <div className="flex items-center gap-2">
            <select className="border rounded px-2 py-2 text-sm bg-background flex-1" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Pick a campaign…</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button size="sm" disabled={!pick || busy} onClick={activate}>Activate</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const LeadDrafts = ({ leadId }: { leadId: string }) => {
  const notify = useNotify();
  const [drafts, setDrafts] = useState<any[]>([]);
  const load = () => { getSupabaseClient().from("drafts").select("*").eq("lead_id", leadId).eq("status", "pending")
    .then(({ data }) => setDrafts(data ?? [])); };
  useEffect(() => { load(); }, [leadId]);
  if (!drafts.length) return null;

  const send = async (id: string) => {
    const { data: sess } = await getSupabaseClient().auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-draft`, {
      method: "POST",
      headers: { apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY as string, Authorization: `Bearer ${sess.session?.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ draft_id: id }),
    });
    const d = await res.json();
    if (!res.ok) { notify(`Send failed: ${d.error}`, { type: "error" }); return; }
    notify("Sent & logged", { type: "success" }); load();
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Drafted by the engine — approve to send</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {drafts.map((d) => (
          <div key={d.id} className="bg-amber-50 border border-amber-200 rounded p-2">
            <div className="text-[0.65rem] font-bold uppercase text-amber-700">{d.channel} · draft-for-approval</div>
            <div className="text-sm text-amber-900 mt-1">{d.body}</div>
            <Button size="sm" className="mt-2" disabled={!d.to_address} onClick={() => send(d.id)}>Approve &amp; send</Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const DealAction = ({ record }: { record: any }) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<{ id: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getSupabaseClient().from("deals").select("id").eq("lead_id", record.id).eq("active", true).maybeSingle()
      .then(({ data }) => setDeal(data));
  }, [record.id]);

  const eligible = record.status === "qualified" || record.status === "handoff";
  if (deal) return <Button variant="secondary" size="sm" onClick={() => navigate(`/deals/${deal.id}`)}>Open deal →</Button>;
  if (!eligible) return null;

  const create = async () => {
    setBusy(true);
    const { data, error } = await getSupabaseClient().rpc("create_deal_from_lead", { p_lead_id: record.id });
    setBusy(false);
    if (error) { notify(`Could not create deal: ${error.message}`, { type: "error" }); return; }
    notify("Deal created — handed to Acquisitions", { type: "success" });
    navigate(`/deals/${data}`);
  };
  return <Button size="sm" disabled={busy} onClick={create}>{busy ? "Creating…" : "Create deal →"}</Button>;
};

// S6: provenance — where this promoted lead came from in Marketing (the raw lead:
// campaign + lists + vintage). Read-only; links back to List Stacking.
const MarketingProvenance = ({ leadId }: { leadId: string }) => {
  const notify = useNotify();
  const [p, setP] = useState<any>(null);
  const [canPush, setCanPush] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getSupabaseClient()
      .from("raw_leads")
      .select("id,vintage_date,case_type,source_channel,campaigns(name),raw_lead_lists(lists(title))")
      .eq("promoted_lead_id", leadId)
      .maybeSingle()
      .then(({ data }) => setP(data));
    getSupabaseClient().rpc("capability_level", { p_cap: "push_to_acquisitions" }).then(({ data }) => setCanPush(data === "full"));
  }, [leadId]);
  if (!p) return null;
  const campaign = Array.isArray(p.campaigns) ? p.campaigns[0]?.name : p.campaigns?.name;
  const lists = (p.raw_lead_lists ?? []).map((m: any) => (Array.isArray(m.lists) ? m.lists[0]?.title : m.lists?.title)).filter(Boolean);
  const demote = async () => {
    if (!confirm("Return this lead to Marketing? It leaves S&A and re-enters the dial queues.")) return;
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("demote_from_acquisitions", { p_raw_lead_id: p.id });
    setBusy(false);
    if (error) return notify(`Return failed: ${error.message}`, { type: "error" });
    notify("Returned to Marketing", { type: "success" });
    window.location.hash = "#/list-stacking";
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Marketing provenance</CardTitle>
        {canPush && <Button size="sm" variant="outline" disabled={busy} onClick={demote}>{busy ? "…" : "Return to Marketing"}</Button>}
      </CardHeader>
      <CardContent className="text-sm space-y-1">
        <div><span className="text-muted-foreground">Case type: </span>{p.case_type || "—"} · <span className="text-muted-foreground">Source: </span>{p.source_channel || "—"}</div>
        <div><span className="text-muted-foreground">Campaign: </span>{campaign || "—"}</div>
        <div><span className="text-muted-foreground">Lists: </span>{lists.length ? lists.join(", ") : "—"}</div>
        <div><span className="text-muted-foreground">Vintage: </span>{p.vintage_date || "—"}</div>
        <a className="text-primary hover:underline text-xs" href="/#/list-stacking">← View in List Stacking</a>
      </CardContent>
    </Card>
  );
};

const LeadShowContent = () => {
  const record = useRecordContext<any>();
  const { call } = useTelephony();
  if (!record) return null;
  return (
    <div className="p-4 grid gap-4 md:grid-cols-3">
      <div className="md:col-span-2 space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle>{record.contact_name || "(no name)"}</CardTitle>
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className="capitalize">{String(record.status).replace("_", " ")}</Badge>
              {record.temperature && <Badge className={`capitalize ${tempColor[record.temperature] ?? ""}`}>{record.temperature}</Badge>}
              {record.next_action_at && (
                <span className="text-xs text-muted-foreground">next: {new Date(record.next_action_at).toLocaleDateString()}</span>
              )}
              <DealAction record={record} />
            </div>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div><span className="text-muted-foreground">Property: </span>
              {[record.address_line1, record.city, record.state, record.zip].filter(Boolean).join(", ") || "—"}</div>
            <div><span className="text-muted-foreground">County: </span>{record.county || "—"}</div>
            <div><span className="text-muted-foreground">Source: </span>{record.source_channel || "—"}</div>
            <div><span className="text-muted-foreground">Attempts: </span>{record.call_attempts ?? 0} calls · {record.sms_attempts ?? 0} texts</div>
            {record.promoted_to_acquisitions_at && (
              <div className="text-xs text-emerald-700">Promoted to Acquisitions {new Date(record.promoted_to_acquisitions_at).toLocaleDateString()}</div>
            )}
          </CardContent>
        </Card>

        <MarketingProvenance leadId={record.id} />

        <Card>
          <CardHeader><CardTitle className="text-base">Qualify — 4 Pillars</CardTitle></CardHeader>
          <CardContent><Qualify record={record} /></CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Activity</CardTitle></CardHeader>
          <CardContent><ActivityLog leadId={record.id} /></CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <LeadDrafts leadId={record.id} />
        <Card>
          <CardHeader><CardTitle className="text-base">Numbers</CardTitle></CardHeader>
          <CardContent><Phones contactId={record.primary_contact_id} onCall={(n) => call(n, record.id)} /></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
          <CardContent><ContactActions record={record} /></CardContent>
        </Card>
        <LeadDrip record={record} />
        <LeadScripts record={record} />
      </div>
    </div>
  );
};

// Caller's mid-call script picker (D34). Read-only: pick an active script and it
// renders (merge fields) against THIS lead via render_template. Never writes.
const LeadScripts = ({ record }: { record: any }) => {
  const [scripts, setScripts] = useState<any[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [rendered, setRendered] = useState<string | null>(null);

  useEffect(() => {
    getSupabaseClient().from("scripts").select("id,name,body,script_type,lead_type")
      .eq("is_active", true).order("sort_order").order("name")
      .then(({ data }) => setScripts(data ?? []));
  }, []);

  const pick = async (id: string) => {
    setSelected(id);
    setRendered(null);
    const s = scripts.find((x) => x.id === id);
    if (!s) return;
    const { data } = await getSupabaseClient().rpc("render_template", { p_body: s.body, p_lead_id: record.id });
    setRendered((data as string) ?? s.body);
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Call scripts</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {scripts.length === 0 ? (
          <div className="text-xs text-muted-foreground">No scripts yet — add them in Settings → Scripts.</div>
        ) : (
          <select className="border rounded px-2 py-2 text-sm bg-background w-full" value={selected} onChange={(e) => pick(e.target.value)}>
            <option value="">Pick a script…</option>
            {scripts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.script_type ? `[${s.script_type}] ` : ""}{s.name}{s.lead_type ? ` · ${s.lead_type}` : ""}
              </option>
            ))}
          </select>
        )}
        {rendered != null && (
          <div className="rounded border bg-muted/40 p-2 text-sm whitespace-pre-wrap max-h-72 overflow-auto">{rendered}</div>
        )}
      </CardContent>
    </Card>
  );
};

const LeadShow = () => (
  <ShowBase resource="leads">
    <LeadShowContent />
  </ShowBase>
);

export default LeadShow;
